import { ExportPngInfo, InsightRendererProps } from '@ecosystem/newron-client-collections';
import { FC, useEffect } from 'react';
import { Provider } from 'react-redux';
import { renderLoading } from '@pages/Loading';
import { getAverageSellingPriceChart } from '@services/Chart.service';
import Store from '@state/Store';
import { useFetchChartData } from '@utils/collectionUtils/hooks/UseFetchChartData';
import { RetailerOverviewLineChartInsight } from './Model';
import { AverageSellingParam } from '@data-visualization/average-selling-price-chart/Types';
import AveragePriceCard, { Props } from '@pages/overview/average-selling-price-card/AveragePriceCard';
import ExportContextProvider from '@hooks/export/context/ExportContextProvider';
import { useExportImages } from '@hooks/export/UseExportChartImage';

import {
  constructDummyPeriodForV2YTDAndMATWeekly,
  getIsUsingCollectionLiveData,
} from '@utils/collectionUtils/CollectionUtils';
import PgWarningBanner from '../warning-banner/pg-warning-banner/PgWarningBanner';
import { svgToPng } from '@utils/SvgToPng';
import { PageType } from '@utils/pageConfig/Types';
import { generateUniqueChartKeys } from '@data-visualization/utilities/GenerateUniqueChartKeys';

type RetailerOverviewLineChartInsightRendererType = InsightRendererProps<RetailerOverviewLineChartInsight>;

const RetailerOverviewLineChartInsightContainer: FC<RetailerOverviewLineChartInsightRendererType> = ({
  insight,
  onLoaded,
  onExportReady,
}) => {
  const { apiParams, factRevenue, volume, collectionDataType } = insight.data;
  const { isLoading, chartData } = useFetchChartData({
    apiRequest: () => {
      const isUsingCollectionLiveData = getIsUsingCollectionLiveData(apiParams.period, collectionDataType);
      const params: AverageSellingParam = {
        ...apiParams,
        period: isUsingCollectionLiveData
          ? constructDummyPeriodForV2YTDAndMATWeekly(apiParams.period, apiParams.periodType)
          : apiParams.period,
        isUsingCollectionLiveData: isUsingCollectionLiveData,
      };
      return getAverageSellingPriceChart(params);
    },
    onLoaded: onLoaded,
  });

  const { exportImages } = useExportImages(PageType.RETAILER_OVERVIEW);

  const chartImage = exportImages[generateUniqueChartKeys(chartData)];

  useEffect(() => {
    if (chartImage) {
      svgToPng(chartImage)
        .then((image) => {
          const exportPowerPointImages = async (): Promise<ExportPngInfo> => {
            return { images: [{ ...chartImage, dataUrl: `${image}` }] };
          };
          onExportReady({ exportPng: exportPowerPointImages });
        })
        .catch((error) => {
          console.error('Error in svgToPng:', error);
        });
    }
  }, [chartImage]);

  //Export Register End
  const props: Props = {
    data: chartData,
    selectedPeriodicity: apiParams.periodType,
    isLoading: isLoading,
    factRevenue: factRevenue,
    volume: volume,
    isRetailerOverview: false,
    isUsingCollection: true,
  };

  if (isLoading) {
    return renderLoading();
  }

  return <AveragePriceCard {...props} />;
};

export const RetailerOverviewLineChartInsightRenderer: FC<RetailerOverviewLineChartInsightRendererType> = (props) => {
  return (
    <Provider store={Store}>
      <ExportContextProvider>
        <PgWarningBanner
          baseApiParams={props.insight.data.apiParams}
          collectionDataType={props.insight.dataType}
        >
          <RetailerOverviewLineChartInsightContainer {...props} />
        </PgWarningBanner>
      </ExportContextProvider>
    </Provider>
  );
};
